import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFeatureCatalog } from '../../../api/plans.api';
import { PLAN_CURRENCIES } from '../../../lib/planCurrency.constants';
import type { PlanFeatureDefinition } from '../../../types/plan.types';
import type { CustomSubscriptionPayload } from '../../../types/tenant.types';

export const CUSTOM_PLAN_VALUE = 'custom';

export type CustomPlanFormState = {
  monthly_price: string;
  yearly_price: string;
  billing_interval: 'monthly' | 'yearly';
  starts_at: string;
  currency: string;
  features: Record<string, string>;
};

const GROUP_LABELS: Record<string, string> = {
  core: 'الأساسيات',
  catalog: 'المنتجات',
  operations: 'التشغيل',
  sales: 'المبيعات والإيجار',
  purchasing: 'المشتريات',
  finance: 'المالية',
  analytics: 'التقارير والمحاسبة',
  intelligence: 'الذكاء',
  automation: 'المساعد الذكي والأتمتة',
  digital: 'الرقمي',
  organization: 'التنظيم',
  limits: 'حدود الاستخدام',
};

const inputCls =
  'w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 bg-white';

export function todayDateInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function addBillingInterval(start: string, interval: 'monthly' | 'yearly'): string {
  const date = new Date(`${start}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return start;
  }
  if (interval === 'yearly') {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setMonth(date.getMonth() + 1);
  }
  return date.toISOString().slice(0, 10);
}

export function emptyCustomPlanForm(): CustomPlanFormState {
  return {
    monthly_price: '',
    yearly_price: '',
    billing_interval: 'monthly',
    starts_at: todayDateInput(),
    currency: 'EGP',
    features: {},
  };
}

export function customPlanFormToPayload(form: CustomPlanFormState): CustomSubscriptionPayload {
  return {
    monthly_price: Number(form.monthly_price || 0),
    yearly_price: Number(form.yearly_price || 0),
    billing_interval: form.billing_interval,
    starts_at: form.starts_at,
    ends_at: addBillingInterval(form.starts_at, form.billing_interval),
    currency: form.currency,
    features: form.features,
  };
}

type CustomPlanFieldsProps = {
  value: CustomPlanFormState;
  onChange: (next: CustomPlanFormState | ((prev: CustomPlanFormState) => CustomPlanFormState)) => void;
  disabled?: boolean;
};

export default function CustomPlanFields({ value, onChange, disabled }: CustomPlanFieldsProps) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState<PlanFeatureDefinition[]>([]);
  const [catalogError, setCatalogError] = useState('');

  useEffect(() => {
    void fetchFeatureCatalog().then((result) => {
      if (result.ok) {
        setCatalog(result.features);
        setCatalogError('');
        onChange((prev) => ({
          ...prev,
          features: seedFeatures(result.features, prev.features),
        }));
      } else {
        setCatalogError(result.message);
      }
    });
    // Hydrate catalog once when the custom plan fields mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groupedFeatures = useMemo(() => {
    const groups = new Map<string, PlanFeatureDefinition[]>();
    const order = [
      'limits',
      'core',
      'catalog',
      'operations',
      'sales',
      'purchasing',
      'finance',
      'analytics',
      'intelligence',
      'automation',
      'digital',
      'organization',
    ];
    for (const item of catalog) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }
    const ordered = new Map<string, PlanFeatureDefinition[]>();
    for (const key of order) {
      if (groups.has(key)) {
        ordered.set(key, groups.get(key)!);
        groups.delete(key);
      }
    }
    for (const [key, list] of groups.entries()) {
      ordered.set(key, list);
    }
    return ordered;
  }, [catalog]);

  const endsAt = addBillingInterval(value.starts_at, value.billing_interval);

  const setField = <K extends keyof CustomPlanFormState>(key: K, val: CustomPlanFormState[K]) => {
    onChange((prev) => ({ ...prev, [key]: val }));
  };

  const setFeature = (key: string, featureValue: string) => {
    onChange((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: featureValue },
    }));
  };

  const toggleBooleanFeature = (key: string) => {
    const current = value.features[key];
    const enabled = ['true', '1', 'yes', 'enabled'].includes(String(current ?? '').toLowerCase());
    setFeature(key, enabled ? 'false' : 'true');
  };

  return (
    <div className="space-y-4 rounded-xl border border-indigo-100 bg-indigo-50/40 p-4">
      <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
        {t('ateliers.custom_plan.title')}
      </p>
      <p className="text-xs text-indigo-600/80">{t('ateliers.custom_plan.hint')}</p>

      {catalogError ? (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{catalogError}</p>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.monthly_price')}
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={value.monthly_price}
            onChange={(e) => setField('monthly_price', e.target.value)}
            disabled={disabled}
            required
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.yearly_price')}
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={value.yearly_price}
            onChange={(e) => setField('yearly_price', e.target.value)}
            disabled={disabled}
            required
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.interval')}
          </label>
          <div className="flex gap-2">
            {(['monthly', 'yearly'] as const).map((interval) => (
              <button
                key={interval}
                type="button"
                disabled={disabled}
                onClick={() => setField('billing_interval', interval)}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                  value.billing_interval === interval
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                {t(`ateliers.custom_plan.${interval}`)}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.currency')}
          </label>
          <select
            value={value.currency}
            onChange={(e) => setField('currency', e.target.value)}
            disabled={disabled}
            className={inputCls}
          >
            {PLAN_CURRENCIES.map((currency) => (
              <option key={currency.value} value={currency.value}>
                {currency.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.starts_at')}
          </label>
          <input
            type="date"
            value={value.starts_at}
            onChange={(e) => setField('starts_at', e.target.value)}
            disabled={disabled}
            required
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('ateliers.custom_plan.duration')}
          </label>
          <input
            type="text"
            readOnly
            value={
              value.billing_interval === 'yearly'
                ? t('ateliers.custom_plan.duration_yearly')
                : t('ateliers.custom_plan.duration_monthly')
            }
            className={`${inputCls} bg-gray-50 text-gray-600`}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            {t('ateliers.custom_plan.ends_at')}: {endsAt}
          </p>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-800 mb-3">{t('ateliers.custom_plan.features')}</p>
        <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
          {[...groupedFeatures.entries()].map(([group, items]) => (
            <div key={group} className="rounded-xl border border-gray-100 overflow-hidden bg-white">
              <div className="px-3 py-2 bg-gray-50 text-xs font-bold text-gray-600 uppercase tracking-wide">
                {GROUP_LABELS[group] ?? group}
              </div>
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((feature) => {
                  if (feature.type === 'integer') {
                    return (
                      <div key={feature.key}>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{feature.label}</label>
                        <input
                          type="number"
                          min={0}
                          value={value.features[feature.key] ?? '0'}
                          onChange={(e) => setFeature(feature.key, e.target.value)}
                          disabled={disabled}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
                        />
                        <p className="text-[11px] text-gray-400 mt-1">{feature.description}</p>
                      </div>
                    );
                  }

                  const enabled = ['true', '1', 'yes', 'enabled'].includes(
                    String(value.features[feature.key] ?? '').toLowerCase(),
                  );

                  return (
                    <label
                      key={feature.key}
                      className="flex items-start gap-3 rounded-lg border border-gray-100 px-3 py-2 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        onChange={() => toggleBooleanFeature(feature.key)}
                        disabled={disabled}
                        className="mt-0.5 w-4 h-4 accent-indigo-600"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{feature.label}</p>
                        <p className="text-[11px] text-gray-400">{feature.description}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function seedFeatures(
  catalog: PlanFeatureDefinition[],
  existing: Record<string, string>,
): Record<string, string> {
  const next: Record<string, string> = { ...existing };
  for (const item of catalog) {
    if (next[item.key] !== undefined) {
      continue;
    }
    next[item.key] = item.type === 'integer' ? '0' : 'false';
  }
  return next;
}
